/**
 * /product-brief-template — SEO landing page targeting the "product brief
 * template" / "product brief example" / "one page product brief" cluster.
 * KD is low; intent is "give me a structure I can fill in today."
 *
 * Differentiation angle: a product brief is the short, one-page document
 * that comes *before* the PRD — used to align on an idea early, when it's
 * still cheap to change your mind. This page hands the reader the actual
 * brief structure (seven sections, spelled out) so the search gets
 * something usable, then shows the real problem: a brief is true for a
 * week and then drifts away from the PRD and decisions that grow out of
 * it. In Draftlize the decisions inside the brief become cards, link to
 * the downstream PRD, and auto-flag stale when a dependency moves.
 *
 * English-only + inline (no i18n), mirroring DecisionLogTemplate.tsx and
 * the comparison pages. Chinese variant lives in ProductBriefTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function ProductBriefTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/product-brief-template"
      hreflangLocales={['en', 'zh', 'zhtw']}
      documentTitle="Product Brief Template: One Page, Always Current · Draftlize"
      documentDescription="A product brief template you can copy — Problem, Target user, Why now, Proposed solution, Success metric, Scope & non-goals, Open questions. The one-page brief is great for early alignment, but it dies the moment the PRD and decisions move past it. In Draftlize the decisions inside the brief become cards that link to the downstream PRD and auto-flag stale when a dependency changes."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Product Briefs</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A product brief that stays one page —<br/>
            <em>and stays true.</em>
          </h1>
          <p className="lede">
            You came for a <em>product brief template</em>: one page, seven headings, something to paste in and fill out before anyone writes a <a href="/product-requirements-document" style={{ color: 'var(--amber)' }}>full PRD</a>. It's right below — take it. A good brief is the cheapest alignment a team ever buys, because it's short enough to read and early enough to change. But here's the catch nobody warns you about: a brief is true for about a week. The PRD grows past it, decisions get made that contradict it, and the one page that was supposed to be the source of truth becomes the first thing that's wrong. Draftlize keeps the brief one page — and makes the decisions inside it cards that link to the PRD downstream and flag themselves stale the moment a dependency moves.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Seven sections. <em>One page. Copy it anywhere.</em></h2>
          </div>
          <p>This is the whole product brief. It fits on a page on purpose — a brief you have to scroll is a PRD wearing a smaller name tag. Use it in a doc, a wiki, or a markdown file for early alignment, before the idea earns a full spec. The section after this one is about what changes when the brief stops being frozen text and starts staying current on its own.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Problem',
              hint: 'Start here',
              body: 'The pain, in one or two plain sentences — who hurts and how often. Not the solution wearing a problem costume. If you can\'t state the problem without naming your feature, you\'re briefing a solution looking for a justification.',
            },
            {
              k: 'Target user',
              hint: 'Who',
              body: 'The specific person this is for. "Solo founders shipping with an AI agent," not "users." A brief that\'s for everyone is for no one, and every later trade-off gets easier the narrower this is.',
            },
            {
              k: 'Why now',
              hint: 'The timing',
              body: 'What changed that makes this worth doing this quarter and not last year — a new capability, a shift in the market, a cost that finally dropped. The section that separates a real bet from a someday-list item.',
            },
            {
              k: 'Proposed solution',
              hint: 'The shape',
              body: 'The approach in a paragraph, not a spec. Enough that a reader pictures the same thing you do, vague enough that you haven\'t pre-committed every detail before you\'ve decided it\'s worth building.',
            },
            {
              k: 'Success metric',
              hint: 'How we\'ll know',
              body: 'The one number that tells you this worked, with a target. "40% of new projects seed a brief in week one." If you can\'t name the metric, you can\'t tell shipped from succeeded.',
            },
            {
              k: 'Scope & non-goals',
              hint: 'The edges',
              body: 'What\'s in for v1, and — more importantly — what you are deliberately not doing. Non-goals are where briefs earn their keep: they end the "while we\'re at it" conversations before they start.',
            },
            {
              k: 'Open questions',
              hint: 'The unknowns',
              body: 'The decisions you haven\'t made yet, named honestly. A brief that pretends everything is settled is lying. These are the items that graduate into real decisions — and, downstream, into the PRD.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the brief. Below: why the page goes out of date the week after you write it — and what we do about it.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a product brief <em>goes stale.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">It's a snapshot of week one</h3>
            <p>A brief captures the idea at its earliest, fuzziest moment — which is exactly its job. But it's a photograph, not a feed. The product keeps moving; the brief doesn't. By the time the PRD exists, half the brief is the team's first guess, and nobody goes back to mark which half.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">It detaches from the PRD it spawned</h3>
            <p>The brief is supposed to be the seed the PRD grows from. But once the PRD takes over, the two drift: a scope call changes in the spec, the brief still says the old thing, and a new teammate reads the brief, builds the wrong mental model, and only finds out in review. Nothing connects the page to what came after it.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Its open questions never close out loud</h3>
            <p>Open questions get answered — in a Slack thread, in someone's head, in a meeting nobody minuted. The decision happens; the brief never hears about it. So the one document meant to hold the early thinking quietly stops reflecting what the team actually decided, and stops being worth opening.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same brief, <em>made live.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">The brief is structured, not just prose</h3>
            <p>In Draftlize the brief isn't a blank page — it's the same seven sections as typed cards: Problem, Target user, Why now, Proposed solution, Success metric, Scope &amp; non-goals, Open questions. Each is addressable by ID and citable from a PRD or a thread, so the brief stays the front door instead of becoming a relic.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The brief links to the PRD downstream</h3>
            <p>An open question becomes a decision; the decision feeds the PRD; the PRD cites the brief. They're one connected graph, not three documents that fell out of touch. Change a scope decision and every card that rested on it — in the brief and in the <a href="/prd-template" style={{ color: 'var(--amber)' }}>PRD template</a> below it — flags itself stale automatically, the way a build system invalidates everything downstream of a changed file.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">An AI agent reads and writes it</h3>
            <p>Claude Code or Cursor, over MCP, reads the brief before it drafts the PRD and writes resolved questions back into the same shape. The brief stops being a doc a human skims once at kickoff and becomes context an agent reads on every turn — so the one page stays one page, and stays true.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A brief's whole value is being short and early. Its whole problem is that short, early documents are the first to fall out of date.
        </blockquote>
        <cite>Keep the one page. Let the substrate keep it current.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is a product brief?', a: 'A product brief is a one-page summary written before a full PRD — the problem, the target user, the goal, the rough scope, and the key risks. It is the cheapest alignment a team buys, because it is short enough to read and early enough to change before anyone invests in a detailed spec.' },
            { q: 'What is the difference between a product brief and a PRD?', a: 'A brief is one page and comes first: it aligns everyone on the problem and the bet before the work is scoped. A PRD comes after and goes deep: the requirements, the behavior, the details. The brief earns the PRD — if the one page does not convince anyone, the idea is not ready for a full document.' },
            { q: 'What should a product brief include?', a: 'Usually seven parts: a one-line summary, the problem, the target user, the goal or success metric, the proposed approach, the scope and non-goals, and the open questions or risks. Keep it to a page — a brief you have to scroll is a PRD wearing a smaller name tag.' },
            { q: 'How do you keep a brief from going stale?', a: 'A brief is usually true for about a week: the PRD grows past it and decisions contradict it. Draftlize keeps the brief one page but makes the decisions inside it cards that link to the PRD downstream — so when a scope decision changes, the brief and everything built on it auto-flag stale instead of silently going wrong.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start your product brief <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the template above, or spin up a project and let the agent draft the brief from a paragraph of your idea — then carry it into the PRD and keep every decision, and everything that depends on it, current for you.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
      <TemplateCta template="product-brief" />
    </MarketingShell>
  );
}
